import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CoronavirusOutlinedIcon from '@mui/icons-material/CoronavirusOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { ROUTES } from '../../constants/routes';

const TABS = [
  { id: 'name', label: 'Test Name', Icon: DescriptionOutlinedIcon },
  { id: 'disease', label: 'By Disease', Icon: CoronavirusOutlinedIcon },
  { id: 'speciality', label: 'By Speciality', Icon: PersonOutlineOutlinedIcon },
];

const POPULAR = ['CBC', 'Thyroid', 'HbA1c', 'Vitamin D', 'Lipid Profile', 'Full Body', 'LFT', 'KFT'];

const DISEASES = [
  'Diabetes', 'Thyroid Disorders', 'Anaemia', 'Vitamin D Deficiency', 'Liver Disorders',
  'Kidney Disorders', 'Cardiovascular Disease', 'PCOS', 'Infertility', 'Cancer Markers',
  'Hypertension', 'Osteoporosis', 'Obesity / Metabolic',
];

const SPECIALITIES = [
  'Cardiologist', 'Endocrinologist', 'Gynaecologist', 'Haematologist', 'Hepatologist',
  'Nephrologist', 'Neurologist', 'Oncologist', 'Paediatrician', 'Physician / Internist',
  'Pulmonologist', 'Rheumatologist', 'Gastroenterologist', 'Urologist',
];

const selectInput = {
  width: '100%',
  bgcolor: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '12px',
  px: 2.25,
  py: 1.75,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
  appearance: 'none',
  cursor: 'pointer',
  fontFamily: '"Outfit", sans-serif',
};

const TestFinder = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('name');
  const [query, setQuery] = useState('');
  const [disease, setDisease] = useState('');
  const [spec, setSpec] = useState('');

  // Pushes the active tab's term to /tests?q=<term> — Tests page picks it up
  // and auto-runs the search against the EDOS API.
  const handleGo = () => {
    const term = (tab === 'name' ? query : tab === 'disease' ? disease : spec).trim();
    if (!term) return navigate(ROUTES.TESTS);
    navigate(`${ROUTES.TESTS}?q=${encodeURIComponent(term)}`);
  };

  return (
    <Box sx={{ bgcolor: 'var(--forest)', position: 'relative', overflow: 'hidden', py: { xs: 7, md: 9 } }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 70% 80% at 60% 50%, rgba(61,122,82,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        {/* Header — 12-col grid: title block=8, CTA=4 */}
        <Grid container columns={12} columnSpacing={2} rowSpacing={2} alignItems="center" sx={{ mb: 3.5 }}>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.25,
                lineHeight: 1,
              }}
            >
              <Box sx={{ width: 18, height: 1.5, bgcolor: 'var(--sage-l)' }} />
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--sage-l)',
                  lineHeight: 1,
                }}
              >
                TrustLab Directory of Services
              </Typography>
            </Box>
            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
                fontWeight: 500,
                fontStyle: 'italic',
                color: '#fff',
                lineHeight: 1.15,
                mb: 1,
              }}
            >
              Find any test, disease or speciality
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 12, md: 13 },
                color: 'rgba(255,255,255,0.4)',
                fontWeight: 300,
              }}
            >
              Search by test name · disease condition · medical speciality
            </Typography>
          </Grid>
        </Grid>

        {/* Search card */}
        <Box
          sx={{
            bgcolor: 'rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px',
            p: { xs: 2.25, md: 3 },
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Tabs — 12-col grid not ideal for variable chip widths, use flex but keep centered wrap */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2.5,
            }}
          >
            {TABS.map((t) => {
              const active = tab === t.id;
              const Icon = t.Icon;
              return (
                <Box
                  key={t.id}
                  component="button"
                  onClick={() => setTab(t.id)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    bgcolor: active ? 'rgba(106,174,128,0.22)' : 'rgba(255,255,255,0.05)',
                    border: '1px solid',
                    borderColor: active ? 'rgba(106,174,128,0.45)' : 'rgba(255,255,255,0.1)',
                    borderRadius: 100,
                    py: 0.875,
                    px: 1.75,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: active ? 'var(--sage-l)' : 'rgba(255,255,255,0.55)',
                    cursor: 'pointer',
                    fontFamily: '"Outfit", sans-serif',
                    lineHeight: 1,
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.25)' },
                  }}
                >
                  <Icon sx={{ fontSize: 14, display: 'block' }} />
                  <Box component="span" sx={{ lineHeight: 1 }}>{t.label}</Box>
                </Box>
              );
            })}
          </Box>

          {/* Tab body — name */}
          {tab === 'name' && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    bgcolor: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    px: 2,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    transition: 'border-color 0.2s',
                    '&:focus-within': { borderColor: 'rgba(200,237,216,0.4)' },
                  }}
                >
                  <SearchIcon sx={{ fontSize: 18, opacity: 0.5, color: '#fff', display: 'block', flexShrink: 0 }} />
                  <Box
                    component="input"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g. CBC, Thyroid, HbA1c, Lipid Profile, Vitamin D…"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: '100%',
                      bgcolor: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: '#fff',
                      fontSize: 14,
                      fontFamily: '"Outfit", sans-serif',
                      '&::placeholder': { color: 'rgba(255,255,255,0.35)' },
                    }}
                  />
                </Box>
                <Button
                  onClick={handleGo}
                  sx={{
                    flexShrink: 0,
                    height: 48,
                    bgcolor: 'var(--amber)',
                    color: '#fff',
                    borderRadius: '12px',
                    px: 3,
                    fontSize: 14,
                    fontWeight: 700,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#C77C1D' },
                  }}
                >
                  Search Tests →
                </Button>
              </Box>

              {/* Popular chips */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.875,
                  mt: 2,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.3)',
                    fontWeight: 600,
                    mr: 0.5,
                    lineHeight: 1,
                  }}
                >
                  Popular:
                </Typography>
                {POPULAR.map((p) => (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => {
                      setQuery(p);
                      navigate(`${ROUTES.TESTS}?q=${encodeURIComponent(p)}`);
                    }}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 100,
                      py: 0.5,
                      px: 1.375,
                      fontSize: 11.5,
                      fontWeight: 500,
                      color: 'rgba(255,255,255,0.55)',
                      cursor: 'pointer',
                      fontFamily: '"Outfit", sans-serif',
                      lineHeight: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(200,237,216,0.1)',
                        borderColor: 'rgba(200,237,216,0.25)',
                        color: 'var(--sage-l)',
                      },
                    }}
                  >
                    {p}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Tab body — disease */}
          {tab === 'disease' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Box
                component="select"
                value={disease}
                onChange={(e) => setDisease(e.target.value)}
                sx={{ ...selectInput, flex: 1, minWidth: 0, height: 48, py: 0 }}
              >
                <option value="" style={{ background: '#1E3D2A' }}>— Choose a disease or condition —</option>
                {DISEASES.map((d) => (
                  <option key={d} value={d} style={{ background: '#1E3D2A' }}>{d}</option>
                ))}
              </Box>
              <Button
                onClick={handleGo}
                sx={{
                  flexShrink: 0,
                  height: 48,
                  bgcolor: 'var(--amber)',
                  color: '#fff',
                  borderRadius: '12px',
                  px: 3,
                  fontSize: 14,
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#C77C1D' },
                }}
              >
                Find Tests →
              </Button>
            </Box>
          )}

          {/* Tab body — speciality */}
          {tab === 'speciality' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Box
                component="select"
                value={spec}
                onChange={(e) => setSpec(e.target.value)}
                sx={{ ...selectInput, flex: 1, minWidth: 0, height: 48, py: 0 }}
              >
                <option value="" style={{ background: '#1E3D2A' }}>— Choose a speciality —</option>
                {SPECIALITIES.map((s) => (
                  <option key={s} value={s} style={{ background: '#1E3D2A' }}>{s}</option>
                ))}
              </Box>
              <Button
                onClick={handleGo}
                sx={{
                  flexShrink: 0,
                  height: 48,
                  bgcolor: 'var(--amber)',
                  color: '#fff',
                  borderRadius: '12px',
                  px: 3,
                  fontSize: 14,
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#C77C1D' },
                }}
              >
                Find Tests →
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default TestFinder;
